"use client";

import { Button, SubmitButton } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { StepFooter, StepScrollArea } from "@/components/wizard";
import { cn } from "@/lib/utils";

import { useExportWizard } from "./context";
import { PdfThemeSection } from "./pdf-theme-section";
import { RadioOption } from "./radio-option";
import { CSV_COLUMNS, NAME_MODES, NAME_MODE_RANK } from "./types";

const CHECKBOX_CLASS =
  "mt-px data-[checked]:border-[#FFAF53] data-[checked]:bg-[#FFAF53] data-[checked]:text-black";

export function StepOptions() {
  const {
    includeContacts,
    handleContactsToggle,
    verifiedOnly,
    setVerifiedOnly,
    personalOnly,
    setPersonalOnly,
    personalPreferred,
    setPersonalPreferred,
    creditsPerReveal,
    nameMode,
    nameModeFloor,
    setNameMode,
    includeHighlights,
    highlightsAllowedByOrg,
    setIncludeHighlights,
    hideExporter,
    setHideExporter,
    csvSelected,
    csvColumns,
    toggleCsvColumn,
    pdfSelected,
    theme,
    setTheme,
    setStep,
  } = useExportWizard();

  const csvColumnsMissing = csvSelected && csvColumns.length === 0;

  return (
    <>
      <StepScrollArea contentClassName="gap-3">
        <p className="text-sm text-muted-foreground">
          Configure export options.
        </p>

        {/* Contact options */}
        <div className="flex flex-col gap-2.5 rounded-lg border border-border bg-muted p-2.5">
          <div className="flex items-start gap-2">
            <Checkbox
              id="include-contacts"
              checked={includeContacts}
              onChange={handleContactsToggle}
              className={CHECKBOX_CLASS}
            />
            <div className="flex flex-col">
              <Label htmlFor="include-contacts" className="font-normal">
                Include contact information
              </Label>
              <span className="text-[11px] text-muted-foreground">
                +{creditsPerReveal} credits per new contact reveal
              </span>
            </div>
          </div>
          <div className="ml-6 flex flex-col gap-2">
            <div
              className={cn(
                "flex items-center gap-2",
                !includeContacts && "opacity-50",
              )}
            >
              <Checkbox
                id="verified-only"
                checked={verifiedOnly}
                onChange={setVerifiedOnly}
                disabled={!includeContacts}
                className={CHECKBOX_CLASS}
              />
              <Label
                htmlFor="verified-only"
                className={cn(
                  "text-xs font-normal",
                  !includeContacts && "text-muted-foreground",
                )}
              >
                Verified contacts only
              </Label>
            </div>
            <div
              className={cn(
                "flex items-center gap-2",
                !includeContacts && "opacity-50",
              )}
            >
              <Checkbox
                id="personal-only"
                checked={personalOnly}
                onChange={setPersonalOnly}
                disabled={!includeContacts}
                className={CHECKBOX_CLASS}
              />
              <Label
                htmlFor="personal-only"
                className={cn(
                  "text-xs font-normal",
                  !includeContacts && "text-muted-foreground",
                )}
              >
                Personal emails only
              </Label>
            </div>
            <div
              className={cn(
                "flex items-center gap-2",
                !includeContacts && "opacity-50",
              )}
            >
              <Checkbox
                id="personal-preferred"
                checked={personalPreferred}
                onChange={setPersonalPreferred}
                disabled={!includeContacts}
                className={CHECKBOX_CLASS}
              />
              <Label
                htmlFor="personal-preferred"
                className={cn(
                  "text-xs font-normal",
                  !includeContacts && "text-muted-foreground",
                )}
              >
                Personal emails preferred
              </Label>
            </div>
          </div>
        </div>

        {/* Candidate names — same options as the org privacy policy, which
            acts as a floor: looser modes than the org setting are locked. */}
        <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-muted p-2.5">
          <span className="text-sm text-foreground">Candidate names</span>
          <div
            role="radiogroup"
            aria-label="Candidate names"
            className="flex flex-col"
          >
            {NAME_MODES.map((mode) => (
              <RadioOption
                key={mode.value}
                label={mode.label}
                description={mode.description}
                selected={nameMode === mode.value}
                disabled={
                  NAME_MODE_RANK[mode.value] < NAME_MODE_RANK[nameModeFloor]
                }
                onSelect={() => setNameMode(mode.value)}
              />
            ))}
          </div>
          {nameModeFloor !== "none" && (
            <span className="text-[11px] text-muted-foreground">
              Your organization&apos;s privacy policy sets the minimum level.
            </span>
          )}
        </div>

        {/* Output options */}
        <div className="flex flex-col gap-2.5 rounded-lg border border-border bg-muted p-2.5">
          <div
            className={cn(
              "flex items-start gap-2",
              !highlightsAllowedByOrg && "opacity-50",
            )}
          >
            <Checkbox
              id="include-highlights"
              checked={includeHighlights}
              onChange={setIncludeHighlights}
              disabled={!highlightsAllowedByOrg}
              className={CHECKBOX_CLASS}
            />
            <div className="flex flex-col">
              <Label
                htmlFor="include-highlights"
                className={cn(
                  "font-normal",
                  !highlightsAllowedByOrg && "text-muted-foreground",
                )}
              >
                Include AI highlights
              </Label>
              <span className="text-[11px] text-muted-foreground">
                {highlightsAllowedByOrg
                  ? "Profile spotlight section in PDF exports"
                  : "Disabled by your organization's privacy policy"}
              </span>
            </div>
          </div>
          <div className="h-px bg-border" />
          <div className="flex items-start gap-2">
            <Checkbox
              id="hide-exporter"
              checked={hideExporter}
              onChange={setHideExporter}
              className={CHECKBOX_CLASS}
            />
            <div className="flex flex-col">
              <Label htmlFor="hide-exporter" className="font-normal">
                Hide exporter name &amp; date
              </Label>
              <span className="text-[11px] text-muted-foreground">
                Removes who exported and when from the output
              </span>
            </div>
          </div>
        </div>

        {pdfSelected && <PdfThemeSection theme={theme} onChange={setTheme} />}

        {/* CSV columns — only when a CSV artifact is selected */}
        {csvSelected && (
          <div className="flex flex-col rounded-lg border border-border bg-muted p-2.5">
            <span className="text-sm font-medium text-foreground">
              CSV columns
            </span>
            <span className="text-[11px] mb-2 text-muted-foreground">
              Pick the candidate data the spreadsheet should include.
            </span>
            <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-2">
              {CSV_COLUMNS.map((column) => (
                <div key={column.key} className="flex items-start gap-2">
                  <Checkbox
                    id={`csv-col-${column.key}`}
                    checked={csvColumns.includes(column.key)}
                    onChange={() => toggleCsvColumn(column.key)}
                    className={CHECKBOX_CLASS}
                  />
                  <Label
                    htmlFor={`csv-col-${column.key}`}
                    className="text-xs font-normal"
                  >
                    {column.label}
                  </Label>
                </div>
              ))}
            </div>
            {csvColumnsMissing && (
              <span className="text-[11px] text-destructive">
                Select at least one column to continue.
              </span>
            )}
          </div>
        )}
      </StepScrollArea>
      <StepFooter>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setStep(1)}
        >
          Back
        </Button>
        <SubmitButton
          type="button"
          size="sm"
          onClick={() => setStep(3)}
          disabled={csvColumnsMissing}
          className="bg-[#FFAF53] border-[#e89a3a] text-black hover:bg-[#F5A347] active:bg-[#E8993A]"
        >
          Continue
        </SubmitButton>
      </StepFooter>
    </>
  );
}
